import { getToken, onMessage, Messaging } from 'firebase/messaging';
import { getFirebaseMessaging, getFirebaseApp, getFCMToken, listenForForegroundMessages } from './firebase';
import FirebasetokenManager from './firebasetokenManager';
import { getFirebaseEnvConfig, getFirebaseConfig } from '@/config/firebase';


interface FirebasePushServiceConfig {
    vapidKey: string;
    retryAttempts?: number;
    retryDelay?: number;
    tokenRefreshInterval?: number;
}

class FirebasePushService {
    private messaging: Messaging | null = null;
    private tokenRefreshTimer: NodeJS.Timeout | null = null;
    private isInitialized = false;
    private retryCount = 0;

    private config: FirebasePushServiceConfig = {
        vapidKey: getFirebaseEnvConfig().vapidKey,
        retryAttempts: 3,
        retryDelay: 2000,
        tokenRefreshInterval: 1000 * 60 * 30 // 30分钟
    };

    /**
     * 检查环境是否支持 Firebase 推送
     */
    private isEnvironmentSupported(): boolean {
        // 检查是否在 APK 环境中
        if (window.platformGame || window.jsBridge) {
            console.log('APK 环境中不需要注册 Firebase 推送');
            return false;
        }

        // 检查协议和环境
        const isSupportedProtocol = location.protocol.startsWith('https') ||
            location.protocol.startsWith('wss') ||
            location.hostname.startsWith('localhost');

        if (!isSupportedProtocol) {
            console.log('当前协议不支持 Firebase 推送');
            return false;
        }

        // 检查浏览器支持
        if (!('serviceWorker' in navigator)) {
            console.log('浏览器不支持 Service Worker');
            return false;
        }

        if (!('Notification' in window)) {
            console.log('浏览器不支持通知功能');
            return false;
        }

        return true;
    }

    /**
     * 初始化 Firebase Messaging
     */
    private initializeMessaging(): boolean {
        try {
            this.messaging = getFirebaseMessaging();
            if (this.messaging) {
                console.log('Firebase Cloud Messaging 初始化成功');
                getFCMToken().then((token) => {
                    console.log('FCM Token 获取成功', token);
                })
                return true;
            } else {
                console.error('Firebase Messaging 初始化失败');
                return false;
            }
        } catch (error) {
            console.error('Firebase Messaging 初始化失败:', error);
            return false;
        }
    }

    /**
     * 检查 Service Worker 是否已注册
     */
    private async checkServiceWorkerRegistration(): Promise<boolean> {
        try {
            const registration = await navigator.serviceWorker.getRegistration();
            if (registration && registration.active) {
                console.log('Service Worker 已注册并激活');
                return true;
            } else {
                console.log('Service Worker 未注册或未激活');
                return false;
            }
        } catch (error) {
            console.error('检查 Service Worker 注册状态失败:', error);
            return false;
        }
    }

    /**
     * 初始化 Firebase 推送服务
     */
    async initialize(): Promise<boolean> {
        if (this.isInitialized) {
            console.log('Firebase 推送服务已经初始化');
            return true;
        }

        if (!this.isEnvironmentSupported()) {
            return false;
        }

        try {
            // 1. 初始化 Firebase Messaging
            if (!this.initializeMessaging()) {
                return false;
            }

            // 2. 检查 Service Worker 是否已注册（由 app.ts 负责注册）
            const hasServiceWorker = await this.checkServiceWorkerRegistration();
            if (!hasServiceWorker) {
                console.log('等待 Service Worker 注册...');
                // 等待一段时间，让 app.ts 有机会注册 Service Worker
                await new Promise(resolve => setTimeout(resolve, 2000));
                const retryHasServiceWorker = await this.checkServiceWorkerRegistration();
                if (!retryHasServiceWorker) {
                    console.log('Service Worker 仍未注册，初始化失败');
                    return false;
                }
            }
            // 3. 检查通知权限
            if (Notification.permission === 'granted') {
                await this.setupMessaging();
            } else if (Notification.permission !== 'denied') {
                const hasPermission = await this.requestNotificationPermission();
                if (hasPermission) {
                    await this.setupMessaging();
                } else {
                    console.log('用户拒绝了通知权限');
                    return false;
                }
            } else {
                console.log('通知权限已被用户明确拒绝');
                return false;
            }

            this.isInitialized = true;
            return true;
        } catch (error) {
            console.error('Firebase 推送服务初始化失败:', error);
            return false;
        }
    }

    /**
     * 设置消息推送
     */
    private async setupMessaging(): Promise<void> {
        // 获取初始 Token
        const token = await this.getFCMToken();
        this.updateTokenInManager(token);

        // 设置 Token 刷新定时器
        this.setupTokenRefreshTimer();

        // 设置前台消息监听
        this.setupForegroundMessageListener();

        console.log('Firebase 推送服务设置完成');
    }

    /**
     * 设置前台消息监听
     */
    private setupForegroundMessageListener(): void {
        const unsubscribe = listenForForegroundMessages((payload) => {
            console.log('收到前台消息:', payload);
            this.handleForegroundMessage(payload);
        });

        if (!unsubscribe) {
            console.error('设置前台消息监听失败');
        }
    }

    /**
     * 处理前台消息
     */
    private handleForegroundMessage(payload: any): void {
        // 这里可以添加自定义的消息处理逻辑
        // 比如显示通知、更新UI等
        console.log('处理前台消息:', payload);
    }

    /**
     * 获取当前 Token
     */
    getCurrentToken(): string | null {
        return FirebasetokenManager.getToken();
    }

    /**
     * 手动刷新 Token
     */
    async refreshToken(): Promise<string | null> {
        console.log('手动刷新 FCM Token...');
        const token = await this.getFCMToken();
        this.updateTokenInManager(token);
        return token;
    }

    /**
     * 更新 Token 到管理器
     */
    private updateTokenInManager(token: string | null): void {
        if (token) {
            const firebaseConfig = getFirebaseConfig();
            FirebasetokenManager.setToken(token);
            FirebasetokenManager.setFirebaseProjectId(firebaseConfig.projectId);
            FirebasetokenManager.setUpdateToken(true);
            console.log('Token 已更新到管理器');
        } else {
            console.warn('Token 为空，无法更新到管理器');
        }
    }

    /**
     * 设置 Token 刷新定时器
     */
    private setupTokenRefreshTimer(): void {
        // 清除现有的定时器
        if (this.tokenRefreshTimer) {
            clearInterval(this.tokenRefreshTimer);
        }

        // 设置新的定时器
        this.tokenRefreshTimer = setInterval(async () => {
            console.log('开始刷新 FCM Token...');
            const token = await this.getFCMToken();
            this.updateTokenInManager(token);
        }, this.config.tokenRefreshInterval);

        console.log(`Token 刷新定时器已设置，间隔: ${this.config.tokenRefreshInterval / 1000}秒`);
    }

    /**
     * 请求通知权限
     */
    private async requestNotificationPermission(): Promise<boolean> {
        try {
            const permission = await Notification.requestPermission();
            return permission === 'granted';
        } catch (error) {
            console.error('请求通知权限失败:', error);
            return false;
        }
    }

    /**
     * 获取 FCM Token（带重试机制）
     */
    private async getFCMToken(): Promise<string | null> {
        for (let attempt = 1; attempt <= this.config.retryAttempts; attempt++) {
            try {
                const token = await getFCMToken();
                if (token) {
                    console.log(`FCM Token 获取成功 (尝试 ${attempt}/${this.config.retryAttempts})`);
                    return token;
                } else {
                    console.warn(`FCM Token 获取失败 (尝试 ${attempt}/${this.config.retryAttempts})`);
                }
            } catch (error) {
                console.error(`获取 FCM Token 出错 (尝试 ${attempt}/${this.config.retryAttempts}):`, error);
            }

            // 如果不是最后一次尝试，等待后重试
            if (attempt < this.config.retryAttempts) {
                await new Promise(resolve => setTimeout(resolve, this.config.retryDelay));
            }
        }

        console.error('FCM Token 获取失败，已达到最大重试次数');
        return null;
    }

    /**
     * 销毁服务
     */
    destroy(): void {
        if (this.tokenRefreshTimer) {
            clearInterval(this.tokenRefreshTimer);
            this.tokenRefreshTimer = null;
        }

        this.isInitialized = false;
        console.log('Firebase 推送服务已销毁');
    }

    /**
     * 检查服务状态
     */
    getStatus(): {
        isInitialized: boolean;
        hasToken: boolean;
        notificationPermission: string;
    } {
        return {
            isInitialized: this.isInitialized,
            hasToken: FirebasetokenManager.hasToken(),
            notificationPermission: Notification.permission
        };
    }
}

// 创建单例实例
const firebasePushService = new FirebasePushService();

export const useFirebasePushInApp = () => {
    /**
     * 初始化 Firebase 推送服务
     */
    const initializeFirebasePush = async () => {
        try {

            console.log('开始初始化 Firebase 推送服务...');
            const success = await firebasePushService.initialize();

            if (success) {
                console.log('Firebase 推送服务初始化成功');

                // 获取服务状态
                const status = firebasePushService.getStatus();
                console.log('服务状态:', status);
                if (!status.hasToken) {
                    // 获取当前 Token
                    const token = firebasePushService.getCurrentToken();
                    if (token) {
                        console.log('当前 FCM Token:', token);
                    }
                }
            } else {
                console.warn('Firebase 推送服务初始化失败');
            }
        } catch (error) {
            console.error('初始化 Firebase 推送服务时出错:', error);
        }
    };

    /**
     * 手动刷新 Token
     */
    const refreshToken = async () => {
        try {
            const token = await firebasePushService.refreshToken();
            if (token) {
                console.log('Token 刷新成功:', token);
            } else {
                console.warn('Token 刷新失败');
            }
        } catch (error) {
            console.error('刷新 Token 时出错:', error);
        }
    };

    /**
     * 获取服务状态
     */
    const getServiceStatus = () => {
        return firebasePushService.getStatus();
    };

    /**
     * 销毁服务（在组件卸载时调用）
     */
    const destroyService = () => {
        firebasePushService.destroy();
    };

    return {
        initializeFirebasePush,
        refreshToken,
        getServiceStatus,
        destroyService
    };
};
